import React from 'react'
import {
  TagOutlined,
  SearchOutlined,
  BookOutlined,
  DollarOutlined,
  HomeOutlined,
  TagsOutlined,
  WalletOutlined,
  UserOutlined,
  FileTextOutlined,
  MessageOutlined,
  ApartmentOutlined,
  FolderOpenOutlined,
  SwapOutlined
} from '@ant-design/icons'
import { getStoreOptions } from './storeUtils'

/**
 * Get username from storage based on login type
 * @returns {string} Username or empty string if not found
 */
export const getUsernameFromStorage = () => {
  try {
    if (localStorage.getItem('loginType') === 'cognito') {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      return userInfo.username || ''
    } else if (localStorage.getItem('loginType') === 'microsoft') {
      const msalUser = JSON.parse(localStorage.getItem('msalUser') || '{}')
      return msalUser.name || msalUser.username || ''
    }
    return ''
  } catch (error) {
    console.error('Error fetching username:', error)
    return ''
  }
}

/**
 * Get user display information
 * @param {Object} cognitoData - Cognito user data
 * @param {Object} graphData - Microsoft Graph API data
 * @returns {Object} User display info
 */
export const getUserDisplayInfo = (cognitoData, graphData) => {
  const loginType = localStorage.getItem('loginType') || 'cognito'

  // Helper function to extract name from email
  const extractNameFromEmail = (email) => {
    if (!email) return null
    const namePart = email.split('@')[0]
    return namePart
      .replace(/[._]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  const displayName = loginType === 'cognito'
    ? cognitoData?.attributes?.name || cognitoData?.username || 'User'
    : graphData?.displayName || graphData?.name || extractNameFromEmail(graphData?.mail) || 'User'

  const userInitial = displayName?.charAt(0).toUpperCase() || 'U'

  return {
    displayName,
    userInitial,
    loginType
  }
}

/**
 * Check if user is logged in with Microsoft AD
 * @returns {boolean} True if logged in with Microsoft, false otherwise
 */
export const isADUser = () => {
  return localStorage.getItem('loginType') === 'microsoft'
}

/**
 * Get cached user access data from localStorage
 * @returns {Object|null} User access data or null if not found
 */
export const getCachedUserAccessData = () => {
  try {
    const cachedData = localStorage.getItem('userAccessData')
    return cachedData ? JSON.parse(cachedData) : null
  } catch (error) {
    console.error('Error parsing cached user access data:', error)
    return null
  }
}

/**
 * Check if user has access to a specific permission
 * @param {string} permissionKey - The permission key to check
 * @returns {boolean} True if user has access, false otherwise
 */
export const hasPermissionAccess = (permissionKey) => {
  const userAccessData = getCachedUserAccessData()

  if (!userAccessData || !userAccessData.message) {
    return false
  }

  const { permissions, accessibleScreens } = userAccessData.message

  // Check if permission is in accessibleScreens array
  if (accessibleScreens && accessibleScreens.includes(permissionKey)) {
    return true
  }

  // Also check permissions object for access property
  if (permissions && permissions[permissionKey] && permissions[permissionKey].access === true) {
    return true
  }

  return false
}

/**
 * Check if a category should be hidden for specific stores
 * @param {string} categoryKey - The category key to check
 * @param {string|number} storeId - The store ID to check against
 * @returns {boolean} True if category should be hidden for this store
 */
export const isCategoryHiddenForStore = (categoryKey, storeId) => {
  if (!storeId) return false
  
  const numericStoreId = parseInt(storeId)
  
  // Gateway store (106) now has access to all categories
  const gatewayHiddenCategories = []
  
  if (numericStoreId === 106 && gatewayHiddenCategories.includes(categoryKey)) {
    return true
  }

  // PH Head Office (1) does not have access to Labelling & Tagging
  if (numericStoreId === 1 && categoryKey === 'labelling') {
    return true
  }
  
  return false
}

/**
 * Check if an app should be hidden for specific stores
 * @param {string} appId - The app ID to check
 * @param {string|number} storeId - The store ID to check against
 * @returns {boolean} True if app should be hidden for this store
 */
export const isAppHiddenForStore = (appId, storeId) => {
  if (!storeId) return false
  
  const numericStoreId = parseInt(storeId)
  
  // Gateway store (106) now has access to all apps
  const gatewayHiddenApps = []
  
  if (numericStoreId === 106 && gatewayHiddenApps.includes(appId)) {
    return true
  }

  // Festival (107) and Evo City (109) only have access to rfp and vendor.
  const rfpVendorOnlyStores = [107, 109]
  const rfpVendorOnlyAllowedApps = ['rfp', 'vendor']

  if (rfpVendorOnlyStores.includes(numericStoreId) && !rfpVendorOnlyAllowedApps.includes(appId)) {
    return true
  }

  if (numericStoreId === 108 && ['shelfEdgeLabels', 'savedLabels'].includes(appId)) {
    return false
  }

  if (numericStoreId === 108 && !rfpVendorOnlyAllowedApps.includes(appId)) {
    return true
  }
  
  return false
}

/**
 * App configurations shared between Home and Navigation
 */
export const appConfig = {
  // Chatbot app - available to all users in all categories
  chatbotApp: {
    id: 'chatbot',
    title: 'Kolumbus.ai',
    icon: React.createElement(MessageOutlined, { className: 'app-icon' }),
    iconBg: '#e100a7',
    path: '/chatBot',
    permissionKey: 'aiChatbot', // Map to API permission key
    requiresPermission: true
  },

  // Base categories available to all users
  baseCategories: [
    {
      title: 'Labelling & Tagging',
      key: 'labelling',
      navIcon: React.createElement(TagsOutlined),
      apps: [
        {
          id: 'shelfEdgeLabels',
          title: 'Shelf Edge Labels',
          icon: React.createElement(TagOutlined, { className: 'app-icon' }),
          iconBg: '#52c41a',
          path: '/shelfEdgeLabels',
          requiresPermission: false // Always accessible
        },
        {
          id: 'savedLabels',
          title: 'Saved Shelf Edge Labels',
          icon: React.createElement(BookOutlined, { className: 'app-icon' }),
          iconBg: '#1890ff',
          path: '/savedShelfEdgeLabels',
          requiresPermission: false // Always accessible
        },
        {
          id: 'clearanceLabels',
          title: 'Add Clearance Labels',
          icon: React.createElement(TagOutlined, { className: 'app-icon' }),
          iconBg: '#ff7a45',
          path: '/clearanceLabels',
          requiresPermission: false // Always accessible
        },
        {
          id: 'viewClearanceLabels',
          title: 'Clearance Reports',
          icon: React.createElement(BookOutlined, { className: 'app-icon' }),
          iconBg: '#238281',
          path: '/viewClearanceLabels',
          requiresPermission: false // Always accessible
        }
      ]
    },
    {
      title: 'Lookup',
      key: 'lookup',
      navIcon: React.createElement(SearchOutlined),
      apps: [
        {
          id: 'productLookup',
          title: 'Product Lookup',
          icon: React.createElement(SearchOutlined, { className: 'app-icon' }),
          iconBg: '#faad14',
          path: '/productLookup',
          requiresPermission: false
        }
      ]
    },
    {
      title: 'Anko.com',
      key: 'contentManagement',
      navIcon: React.createElement(ApartmentOutlined),
      apps: [
        {
          id: 'contentManagementCategories',
          title: 'Content Management',
          dashboardTitle: 'Content Management',
          icon: React.createElement(FolderOpenOutlined, { className: 'app-icon' }),
          iconBg: '#5b5bd6',
          path: '/content-management',
          requiresPermission: false
        }
      ]
    },
    {
      title: 'Inventory',
      key: 'inventory',
      navIcon: React.createElement(SwapOutlined),
      apps: [
        {
          id: 'inventoryMovement',
          title: 'Inventory Movement',
          icon: React.createElement(SwapOutlined, { className: 'app-icon' }),
          iconBg: '#1677ff',
          path: '/inventoryMovement',
          permissionKey: 'inventoryMovement', // Map to API permission key
          requiresPermission: true
        }
      ]
    }
  ],

  // AD-only categories
  adOnlyCategories: [
    {
      title: 'Cash Management System',
      key: 'cashManagement',
      navIcon: React.createElement(WalletOutlined),
      apps: [
        {
          id: 'pettycash',
          title: 'Petty Cash',
          icon: React.createElement(DollarOutlined, { className: 'app-icon' }),
          iconBg: '#f5222d',
          path: '/pettyCash',
          permissionKey: 'pettyCash', // Map to API permission key
          requiresPermission: true
        },
        {
          id: 'rfp',
          title: 'Request for Payment',
          icon: React.createElement(FileTextOutlined, { className: 'app-icon' }),
          iconBg: '#13c2c2',
          path: '/rfp',
          permissionKey: 'rfp', // Map to API permission key
          requiresPermission: true
        },
        {
          id: 'vendor',
          title: 'Vendors',
          icon: React.createElement(UserOutlined, { className: 'app-icon' }),
          iconBg: '#722ed1',
          path: '/vendor',
          permissionKey: 'vendor', // Map to API permission key
          requiresPermission: true
        }
      ]
    }
  ],

  // Store options - now using the utility function
  stores: getStoreOptions(),

  // Home menu item (separate from categories)
  homeMenuItem: {
    key: '/home',
    icon: React.createElement(HomeOutlined),
    label: 'Home',
    path: '/home'
  },

  /**
   * Filter apps based on user permissions
   * @param {Array} apps - Array of apps to filter
   * @returns {Array} Filtered apps based on user access
   */
  filterAppsByPermissions: function (apps) {
    return apps.filter(app => {
      // If app doesn't require permission, always show it
      if (!app.requiresPermission) {
        return true
      }

      // If app requires permission, check user access
      if (app.permissionKey) {
        return hasPermissionAccess(app.permissionKey)
      }

      // Default: show app if no permission logic defined
      return true
    })
  },

  /**
   * Get all categories based on user login type and permissions
   * @param {string|number} storeId - Optional store ID for store-specific filtering
   * @returns {Array} All categories available to the current user
   */
  getAllCategories: function (storeId = null) {
    const userCategories = [...this.baseCategories]

    // Add Cash Management System for AD users (no environment restriction)
    if (isADUser()) {
      userCategories.push(...this.adOnlyCategories)
    }

    // Filter categories based on store restrictions
    const storeFilteredCategories = storeId 
      ? userCategories.filter(category => !isCategoryHiddenForStore(category.key, storeId))
      : userCategories

    // Filter apps in each category based on permissions and store restrictions
    const filteredCategories = storeFilteredCategories.map(category => ({
      ...category,
      apps: this.filterAppsByPermissions(category.apps).filter(app => 
        storeId ? !isAppHiddenForStore(app.id, storeId) : true
      )
    })).filter(category => category.apps.length > 0) // Remove empty categories

    // Add the "All" category that includes all visible apps + chatbot
    const allApps = []
    filteredCategories.forEach(category => {
      allApps.push(...category.apps)
    })

    // Add chatbot if user has access and not hidden for store
    const chatbotApps = this.filterAppsByPermissions([this.chatbotApp])
    if (chatbotApps.length > 0) {
      const chatbotHidden = storeId ? isAppHiddenForStore(this.chatbotApp.id, storeId) : false
      if (!chatbotHidden) {
        allApps.push(this.chatbotApp)
      }
    }

    // Only add "All" category if there are apps to show
    const allCategory = {
      title: 'All',
      key: 'all',
      apps: allApps
    }

    const finalCategories = allApps.length > 0 ? [allCategory, ...filteredCategories] : filteredCategories

    return finalCategories
  },

  /**
   * Get accessible apps count for current user
   * @returns {number} Number of accessible apps
   */
  getAccessibleAppsCount: function () {
    const categories = this.getAllCategories()
    let totalApps = 0

    categories.forEach(category => {
      if (category.key !== 'all') { // Don't double count from "All" category
        totalApps += category.apps.length
      }
    })

    return totalApps
  }
}

/**
 * Get navigation menu items based on app config and current user type
 * @param {Function} navigate - React Router navigate function
 * @param {Function} getClassName - Function to get active class name
 * @param {string|number} storeId - Optional current store ID for filtering
 * @returns {Array} Menu items for sidebar navigation
 */
export const getNavigationMenuItems = (navigate, getClassName, storeId = null) => {
  const { homeMenuItem } = appConfig
  const categories = appConfig.getAllCategories(storeId) // Pass store ID

  const homeItem = {
    key: homeMenuItem.key,
    icon: homeMenuItem.icon,
    label: homeMenuItem.label,
    onClick: () => navigate(homeMenuItem.path),
    className: getClassName(homeMenuItem.path)
  }

  const categoryItems = categories
    .filter(category => category.key !== 'all')
    .map(category => ({
      key: category.key,
      icon: category.navIcon,
      label: category.title,
      children: category.apps.map(app => ({
        key: app.path,
        label: app.title,
        onClick: () => navigate(app.path),
        className: getClassName(app.path)
      }))
    }))

  return [homeItem, ...categoryItems]
}